<script setup lang="ts">
/**
 * 对话框。
 *
 * 自己实现（不引 headlessui / radix），但可访问性不能省：
 * - 打开时把焦点移进面板，关闭时还给**触发它的那个元素**（`document.activeElement`
 *   在打开前先存下来）。少了这一步，键盘用户关掉弹窗后焦点会掉回 <body>，
 *   下一次 Tab 从页面开头重新开始。
 * - 面板拦截 Escape 并 `stopPropagation`：外层如果也有 Escape 处理（比如列表页的
 *   "取消重命名"），不应该在弹窗开着时被一起触发。
 * - `body.style.overflow = hidden` 并**在清理时还原原值**，不是无条件置回 ''：
 *   连续开关两个弹窗时，后者若是无条件清空，前者的滚动锁会一起消失。
 *
 * 硬编码的 id `modal-title` / `modal-description` 与原实现一致：
 * 同一时刻只会有一个弹窗，为它引入 id 生成器不划算。
 */

import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

import { cn } from '../../lib/cn';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    description?: string;
    tone?: 'default' | 'danger';
  }>(),
  { tone: 'default' },
);

const emit = defineEmits<{ close: [] }>();

const panelRef = ref<HTMLDivElement | null>(null);
let teardown: (() => void) | null = null;

/**
 * 关闭时调用。用 `close` 事件而不是把 `open` 写成 v-model：
 * 弹窗永远不该自己决定"我关掉了"——状态归父组件，这样才能保证
 * "先关再开另一个"时不会出现两个面板同时挂载。
 */
function requestClose(): void {
  emit('close');
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  requestClose();
}

watch(
  () => props.open,
  (open) => {
    // 先无条件清理上一次：连续开关时不能把上一个弹窗的滚动锁留在 body 上。
    teardown?.();
    teardown = null;

    if (!open) return;

    const restoreFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // 保存原值而不是无条件还原成 '':外层可能已经有别的滚动锁。
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    void nextTick(() => {
      const panel = panelRef.value;
      if (panel === null) return;
      const first =
        panel.querySelector<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? panel;
      first.focus();
    });

    teardown = () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreFocusTo?.focus();
    };
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  teardown?.();
  teardown = null;
});
</script>

<template>
  <div v-if="props.open" class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-slate-900/40" aria-hidden="true" @click="requestClose" />
    <div
      ref="panelRef"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      :aria-describedby="props.description === undefined ? undefined : 'modal-description'"
      tabindex="-1"
      class="relative w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl animate-fade-in"
    >
      <h2 id="modal-title" class="text-base font-semibold text-slate-900">{{ props.title }}</h2>
      <p
        v-if="props.description !== undefined"
        id="modal-description"
        class="mt-2 text-sm leading-relaxed text-slate-600"
      >
        {{ props.description }}
      </p>
      <div v-if="$slots.default !== undefined" class="mt-3">
        <slot />
      </div>
      <div :class="cn('mt-5 flex justify-end gap-2', props.tone === 'danger' && 'text-red-700')">
        <slot name="footer">
          <button
            type="button"
            class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
            @click="requestClose"
          >
            知道了
          </button>
        </slot>
      </div>
    </div>
  </div>
</template>
